import { useEffect, useState, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  X, ArrowRight, ArrowLeft, Check, Loader2, Upload, Camera,
  User, Mail, MapPin, Ruler, Sparkles, Instagram, Target, Phone, ShieldCheck,
} from "lucide-react";
import { toast } from "sonner";
import { createApplication, uploadVerificationVideo } from "@/lib/apiClient";
import VideoVerification from "@/components/VideoVerification";
import MoodBoardForm from "@/components/MoodBoardForm";

const EXPERIENCE = [
  { value: "none", label: "Fresh face", hint: "Never shot before." },
  { value: "some", label: "A few shoots", hint: "Some freelance work." },
  { value: "professional", label: "Booked & busy", hint: "Signed or paid work." },
];

const emptyForm = {
  full_name: "", email: "", phone: "", age: "",
  address_line: "", city: "", country: "",
  height_cm: "", measurements: "", experience: "",
  instagram: "", tiktok: "", portfolio_url: "", goals: "",
};

const genCode = () =>
  Math.floor(1000 + Math.random() * 9000).toString();

const fileToDataUrl = (file, maxDim = 900, quality = 0.72) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        let { width, height } = img;
        if (width > height && width > maxDim) { height = Math.round((height * maxDim) / width); width = maxDim; }
        else if (height > maxDim) { width = Math.round((width * maxDim) / height); height = maxDim; }
        const c = document.createElement("canvas");
        c.width = width; c.height = height;
        c.getContext("2d").drawImage(img, 0, 0, width, height);
        resolve(c.toDataURL("image/jpeg", quality));
      };
      img.onerror = reject;
      img.src = e.target.result;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

export default function ApplyMobile({ open, onClose, hideOnDesktop = true }) {
  const [step, setStep] = useState(0);
  const [form, setForm] = useState(emptyForm);
  const [photos, setPhotos] = useState([]);
  const [verificationVideo, setVerificationVideo] = useState(null);
  const [verificationReady, setVerificationReady] = useState(false);
  const [challengeCode, setChallengeCode] = useState(genCode);
  const [uploadProgress, setUploadProgress] = useState(null);
  const uploadedVideoRef = useRef(null);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [applicationId, setApplicationId] = useState(null);
  const [showMoodBoard, setShowMoodBoard] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    if (open) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const steps = [
    {
      key: "name",
      icon: User,
      title: "What should we call you?",
      hint: "Your real name — agents book people, not usernames.",
      render: () => (
        <input
          data-testid="mob-input-name"
          autoFocus
          value={form.full_name}
          onChange={(e) => set("full_name")(e.target.value)}
          placeholder="Ava Bennett"
          className="w-full bg-transparent border-0 border-b border-white/25 text-3xl md:text-4xl font-display font-light text-white placeholder:text-white/25 focus:outline-none focus:border-white pb-3"
        />
      ),
      valid: () => form.full_name.trim().length >= 2 || "Enter your full name.",
    },
    {
      key: "email",
      icon: Mail,
      title: "Where do we send the good news?",
      hint: "Casting calls, contracts, offers — all land here.",
      render: () => (
        <input
          data-testid="mob-input-email"
          type="email"
          inputMode="email"
          autoFocus
          value={form.email}
          onChange={(e) => set("email")(e.target.value)}
          placeholder="you@email.com"
          className="w-full bg-transparent border-0 border-b border-white/25 text-2xl md:text-3xl font-body text-white placeholder:text-white/25 focus:outline-none focus:border-white pb-3"
        />
      ),
      valid: () => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email) || "That doesn't look like an email.",
    },
    {
      key: "phone",
      icon: Phone,
      title: "Best number to reach you.",
      hint: "Required. Scouts often call to confirm castings within 24 hours.",
      render: () => (
        <input
          data-testid="mob-input-phone"
          type="tel"
          inputMode="tel"
          autoComplete="tel"
          autoFocus
          value={form.phone}
          onChange={(e) => set("phone")(e.target.value)}
          placeholder="+91 98xxx xxxxx"
          className="w-full bg-transparent border-0 border-b border-white/25 text-2xl md:text-3xl font-body text-white placeholder:text-white/25 focus:outline-none focus:border-white pb-3"
        />
      ),
      valid: () => {
        const digits = form.phone.replace(/\D/g, "");
        if (digits.length < 7) return "Enter a valid phone number (with country code).";
        return true;
      },
    },
    {
      key: "location",
      icon: MapPin,
      title: "Where in the world are you?",
      hint: "Full address helps for local castings and courier drops. All fields required.",
      render: () => (
        <div className="space-y-6">
          <input
            data-testid="mob-input-address"
            autoFocus
            value={form.address_line}
            onChange={(e) => set("address_line")(e.target.value)}
            placeholder="Street address (house / flat / area)"
            className="w-full bg-transparent border-0 border-b border-white/25 text-lg font-body text-white placeholder:text-white/25 focus:outline-none focus:border-white pb-3"
          />
          <input
            data-testid="mob-input-city"
            value={form.city}
            onChange={(e) => set("city")(e.target.value)}
            placeholder="City"
            className="w-full bg-transparent border-0 border-b border-white/25 text-2xl font-body text-white placeholder:text-white/25 focus:outline-none focus:border-white pb-3"
          />
          <input
            data-testid="mob-input-country"
            value={form.country}
            onChange={(e) => set("country")(e.target.value)}
            placeholder="Country"
            className="w-full bg-transparent border-0 border-b border-white/25 text-2xl font-body text-white placeholder:text-white/25 focus:outline-none focus:border-white pb-3"
          />
          <input
            data-testid="mob-input-age"
            inputMode="numeric"
            value={form.age}
            onChange={(e) => set("age")(e.target.value.replace(/\D/g, ""))}
            placeholder="Age"
            className="w-full bg-transparent border-0 border-b border-white/25 text-2xl font-body text-white placeholder:text-white/25 focus:outline-none focus:border-white pb-3"
          />
        </div>
      ),
      valid: () => {
        if (form.address_line.trim().length < 4) return "Enter your street address.";
        if (form.city.trim().length < 2) return "Enter your city.";
        if (form.country.trim().length < 2) return "Enter your country.";
        return true;
      },
    },
    {
      key: "stats",
      icon: Ruler,
      title: "Your stats?",
      hint: "Optional but useful for casting. All measurements welcome.",
      render: () => (
        <div className="space-y-6">
          <input
            data-testid="mob-input-height"
            autoFocus
            value={form.height_cm}
            onChange={(e) => set("height_cm")(e.target.value)}
            placeholder="Height (cm)"
            className="w-full bg-transparent border-0 border-b border-white/25 text-2xl font-body text-white placeholder:text-white/25 focus:outline-none focus:border-white pb-3"
          />
          <input
            data-testid="mob-input-measurements"
            value={form.measurements}
            onChange={(e) => set("measurements")(e.target.value)}
            placeholder="Measurements  e.g. 86-61-89"
            className="w-full bg-transparent border-0 border-b border-white/25 text-2xl font-body text-white placeholder:text-white/25 focus:outline-none focus:border-white pb-3"
          />
        </div>
      ),
      valid: () => true,
    },
    {
      key: "experience",
      icon: Sparkles,
      title: "Where are you at?",
      hint: "Pick whichever feels most like you today.",
      render: () => (
        <div className="space-y-3">
          {EXPERIENCE.map((o) => {
            const active = form.experience === o.value;
            return (
              <button
                key={o.value}
                data-testid={`mob-exp-${o.value}`}
                onClick={() => set("experience")(o.value)}
                className={`w-full text-left rounded-2xl border p-5 transition-all ${
                  active
                    ? "bg-white text-black border-white"
                    : "bg-transparent text-white border-white/20 hover:border-white/60"
                }`}
              >
                <p className="font-display text-2xl font-light">{o.label}</p>
                <p className={`text-xs mt-1 ${active ? "text-black/60" : "text-white/40"}`}>{o.hint}</p>
              </button>
            );
          })}
        </div>
      ),
      valid: () => true,
    },
    {
      key: "socials",
      icon: Instagram,
      title: "Show us your feed.",
      hint: "Handles or links — anywhere your face already lives.",
      render: () => (
        <div className="space-y-6">
          <input
            data-testid="mob-input-instagram"
            autoFocus
            value={form.instagram}
            onChange={(e) => set("instagram")(e.target.value)}
            placeholder="@instagram"
            className="w-full bg-transparent border-0 border-b border-white/25 text-2xl font-body text-white placeholder:text-white/25 focus:outline-none focus:border-white pb-3"
          />
          <input
            data-testid="mob-input-tiktok"
            value={form.tiktok}
            onChange={(e) => set("tiktok")(e.target.value)}
            placeholder="@tiktok"
            className="w-full bg-transparent border-0 border-b border-white/25 text-2xl font-body text-white placeholder:text-white/25 focus:outline-none focus:border-white pb-3"
          />
          <input
            data-testid="mob-input-portfolio"
            value={form.portfolio_url}
            onChange={(e) => set("portfolio_url")(e.target.value)}
            placeholder="Portfolio / website"
            className="w-full bg-transparent border-0 border-b border-white/25 text-lg font-body text-white placeholder:text-white/25 focus:outline-none focus:border-white pb-3"
          />
        </div>
      ),
      valid: () => true,
    },
    {
      key: "goals",
      icon: Target,
      title: "Where do you want this to take you?",
      hint: "Editorial covers? Runway? Global campaigns? Say it out loud.",
      render: () => (
        <textarea
          data-testid="mob-input-goals"
          autoFocus
          value={form.goals}
          onChange={(e) => set("goals")(e.target.value)}
          rows={5}
          placeholder="I want to…"
          className="w-full bg-transparent border border-white/20 rounded-2xl p-5 text-lg font-body text-white placeholder:text-white/25 focus:outline-none focus:border-white resize-none"
        />
      ),
      valid: () => true,
    },
    {
      key: "photos",
      icon: Camera,
      title: "Add your photos.",
      hint: "Face shot + full body. No filters, natural light. Up to 4.",
      render: () => (
        <div>
          <div className="grid grid-cols-2 gap-3 mb-4">
            {photos.map((p, i) => (
              <div key={i} className="relative aspect-[3/4] overflow-hidden rounded-2xl">
                <img src={p} alt={`upload ${i}`} className="w-full h-full object-cover" />
                <button
                  data-testid={`mob-remove-photo-${i}`}
                  onClick={() => setPhotos((all) => all.filter((_, idx) => idx !== i))}
                  className="absolute top-2 right-2 bg-black/70 rounded-full p-1.5"
                  aria-label="Remove"
                >
                  <X size={14} className="text-white" />
                </button>
              </div>
            ))}
            {photos.length < 4 && (
              <button
                data-testid="mob-upload-btn"
                onClick={() => fileRef.current?.click()}
                className="aspect-[3/4] rounded-2xl border border-dashed border-white/30 flex flex-col items-center justify-center gap-2 text-white/50 active:scale-[0.98] transition-transform"
              >
                <Upload size={22} strokeWidth={1.5} />
                <span className="text-[10px] uppercase tracking-[0.2em]">Add</span>
              </button>
            )}
          </div>
          <input
            ref={fileRef}
            data-testid="mob-file-input"
            type="file"
            accept="image/*"
            multiple
            className="hidden"
            onChange={async (e) => {
              const files = Array.from(e.target.files || []);
              const room = 4 - photos.length;
              if (room <= 0) { toast.error("Maximum of 4 photos."); return; }
              try {
                const encoded = await Promise.all(files.slice(0, room).map((f) => fileToDataUrl(f)));
                setPhotos((p) => [...p, ...encoded]);
              } catch {
                toast.error("Could not read one of the images.");
              }
              if (fileRef.current) fileRef.current.value = "";
            }}
          />
        </div>
      ),
      valid: () => true,
    },
    {
      key: "verify",
      icon: ShieldCheck,
      title: "Quick verification.",
      hint: "A six-second clip for our scouts to review alongside your photos. No ID document or face scan needed.",
      render: () => (
        <VideoVerification
          code={challengeCode}
          value={verificationVideo}
          onCapture={(blob) => { setVerificationReady(false); uploadedVideoRef.current = null; setVerificationVideo(blob); }}
          onClear={() => { setVerificationReady(false); uploadedVideoRef.current = null; setVerificationVideo(null); }}
          onReady={setVerificationReady}
          disabled={submitting}
        />
      ),
      valid: () => !verificationVideo ? "Please record a verification video to continue." : verificationReady || "Please wait for your recording to load, or retake it if it cannot play.",
    },
  ];

  const total = steps.length;
  const current = steps[step];

  const next = () => {
    const v = current.valid();
    if (v !== true) { toast.error(v); return; }
    if (step < total - 1) setStep(step + 1);
    else submit();
  };
  const back = () => setStep((s) => Math.max(0, s - 1));

  const submit = async () => {
    setSubmitting(true);
    try {
      if (!uploadedVideoRef.current) {
        uploadedVideoRef.current = await uploadVerificationVideo(verificationVideo, setUploadProgress);
      }
      const created = await createApplication({
        ...form,
        age: form.age ? parseInt(form.age, 10) : null,
        photos,
        ...uploadedVideoRef.current,
        verification_code: challengeCode,
      });
      setApplicationId(created?.id || null);
      setDone(true);
      toast.success("Application received. We'll be in touch.");
    } catch (err) {
      if ([404, 409, 410].includes(err.response?.status)) uploadedVideoRef.current = null;
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Submission failed. Your form and recording are still here. Please try again.");
    } finally {
      setSubmitting(false);
      setUploadProgress(null);
    }
  };

  const close = () => {
    onClose?.();
    setTimeout(() => {
      setStep(0);
      if (done) {
        setForm(emptyForm);
        setPhotos([]);
        setVerificationVideo(null);
        setVerificationReady(false);
        setChallengeCode(genCode());
        uploadedVideoRef.current = null;
        setApplicationId(null);
        setShowMoodBoard(false);
        setDone(false);
      }
    }, 400);
  };

  const Icon = current?.icon;

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          data-testid="apply-mobile-modal"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
          className={`fixed inset-0 z-[110] ${hideOnDesktop ? "md:hidden" : ""} bg-[#0a0a0a] flex flex-col`}
        >
          {/* Top bar: close + progress */}
          <div className="flex items-center gap-4 px-5 pt-4 pb-2">
            <button
              data-testid="mob-close-btn"
              onClick={close}
              disabled={submitting}
              className="text-white/70 hover:text-white p-2 -ml-2"
              aria-label="Close"
            >
              <X size={22} />
            </button>
            <div className="flex-1 flex gap-1.5">
              {steps.map((_, i) => (
                <span
                  key={i}
                  className={`h-1 flex-1 rounded-full transition-colors duration-500 ${
                    i <= step ? "bg-white" : "bg-white/15"
                  }`}
                />
              ))}
            </div>
            <span className="text-[10px] uppercase tracking-[0.25em] font-bold text-white/40 tabular-nums">
              {step + 1}/{total}
            </span>
          </div>

          {/* Body */}
          <div data-lenis-prevent className="flex-1 overflow-y-auto px-6 pb-40">
            <div className="max-w-lg mx-auto w-full">
            {done ? (
              <motion.div
                data-testid="mob-success"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="pt-12 pb-16"
              >
                {!showMoodBoard ? (
                  <>
                    <div className="w-16 h-16 rounded-full bg-white flex items-center justify-center mb-6">
                      <Check size={30} strokeWidth={2} className="text-black" />
                    </div>
                    <p className="text-[10px] uppercase tracking-[0.35em] font-bold text-white/40 mb-3">
                      Application received
                    </p>
                    <h3 className="font-display text-4xl md:text-5xl font-light tracking-tight leading-[1.05] mb-3 text-white">
                      Congratulations{form.full_name ? `, ${form.full_name.split(" ")[0]}` : ""}.
                    </h3>
                    <p className="font-script text-white text-3xl md:text-4xl leading-none mb-5">
                      your profile is eligible for a free shoot.
                    </p>
                    <p className="text-sm md:text-base text-white/60 leading-relaxed mb-8 max-w-md">
                      Drop the mood boards and dates you&rsquo;re free — our creative team reviews every submission. If your mood board is approved, you&rsquo;ll receive an email confirming your free shoot.
                    </p>
                    <div className="flex flex-col sm:flex-row gap-3">
                      <button
                        data-testid="mob-open-moodboard-btn"
                        onClick={() => setShowMoodBoard(true)}
                        className="inline-flex items-center gap-3 rounded-full bg-white text-black px-8 py-4 text-xs uppercase tracking-[0.25em] font-bold active:scale-[0.98] transition-transform"
                      >
                        Submit mood board <ArrowRight size={14} />
                      </button>
                      <button
                        data-testid="mob-close-success-btn"
                        onClick={close}
              disabled={submitting}
                        className="inline-flex items-center gap-3 rounded-full border border-white/25 text-white px-8 py-4 text-xs uppercase tracking-[0.25em] font-bold active:scale-[0.98] transition-transform"
                      >
                        Maybe later
                      </button>
                    </div>
                  </>
                ) : (
                  <>
                    <p className="text-[10px] uppercase tracking-[0.35em] font-bold text-white/40 mb-3">
                      Your free shoot
                    </p>
                    <h3 className="font-display text-3xl md:text-4xl font-light tracking-tight leading-[1.05] mb-8 text-white">
                      Show us the shoot you&rsquo;d love.
                    </h3>
                    <MoodBoardForm
                      applicationId={applicationId}
                      email={form.email}
                      applicantName={form.full_name}
                      onSuccess={() => {}}
                    />
                    <button
                      type="button"
                      data-testid="mob-close-after-moodboard-btn"
                      onClick={close}
              disabled={submitting}
                      className="mt-8 text-[10px] uppercase tracking-[0.3em] font-bold text-white/40 hover:text-white transition-colors"
                    >
                      Close
                    </button>
                  </>
                )}
              </motion.div>
            ) : (
              <AnimatePresence mode="wait">
                <motion.div
                  key={current.key}
                  initial={{ opacity: 0, x: 24 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -24 }}
                  transition={{ duration: 0.28, ease: "easeOut" }}
                  className="pt-10"
                >
                  <div className="w-12 h-12 rounded-full border border-white/20 flex items-center justify-center mb-6">
                    <Icon size={20} strokeWidth={1.6} className="text-white" />
                  </div>
                  <h2 className="font-display text-4xl sm:text-5xl font-light tracking-tight text-white leading-[1.05] mb-3">
                    {current.title}
                  </h2>
                  <p className="text-sm text-white/50 leading-relaxed mb-10 max-w-sm">
                    {current.hint}
                  </p>
                  {current.render()}
                  {uploadProgress && (
                    <div data-testid="verification-upload-progress" role="status" aria-live="polite" className="mt-6 space-y-2 text-sm text-white/80">
                      <p>{uploadProgress.label} {uploadProgress.percent}%</p>
                      <progress aria-label="Video upload progress" max="100" value={uploadProgress.percent} className="w-full accent-white" />
                    </div>
                  )}
                </motion.div>
              </AnimatePresence>
            )}
            </div>
          </div>

          {/* Sticky bottom action bar */}
          {!done && (
            <div className="absolute bottom-0 left-0 right-0 px-6 pb-8 pt-4 bg-gradient-to-t from-[#0a0a0a] via-[#0a0a0a] to-transparent">
              <div className="max-w-lg mx-auto flex items-center gap-3">
                {step > 0 ? (
                  <button
                    data-testid="mob-back-btn"
                    onClick={back}
                    disabled={submitting}
                    className="p-4 rounded-full border border-white/25 text-white active:scale-[0.96] transition-transform"
                    aria-label="Back"
                  >
                    <ArrowLeft size={18} />
                  </button>
                ) : null}
                <button
                  data-testid="mob-next-btn"
                  onClick={next}
                  disabled={submitting}
                  className="flex-1 flex items-center justify-between rounded-full bg-white text-black px-7 py-4 text-xs uppercase tracking-[0.25em] font-bold active:scale-[0.98] transition-transform disabled:opacity-60"
                >
                  {submitting ? (
                    <><Loader2 size={16} className="animate-spin" /> Sending…<span /></>
                  ) : step === total - 1 ? (
                    <>Submit application <ArrowRight size={18} /></>
                  ) : (
                    <>Continue <ArrowRight size={18} /></>
                  )}
                </button>
              </div>
            </div>
          )}
        </motion.div>
      )}
    </AnimatePresence>
  );
}
